import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout/Layout';
import api from '../../../api/axios';
import { downloadFile } from '../../../utils/download';
import './Quiz.css';

export default function Quiz() {
  const { id } = useParams(); // user_training id
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({}); // { questionId: [answerId, ...] }
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/user-trainings/my'),
      api.get(`/trainings/${null}`).catch(() => null),
    ]);
    // Load training + questions
    api.get('/user-trainings/my').then(res => {
      const ut = res.data.find(t => t.id === Number(id));
      setTraining(ut);
      if (ut) {
        api.get(`/trainings/${ut.trainingId}`).then(tRes => {
          setQuestions(tRes.data.questions || []);
        });
      }
    }).finally(() => setLoading(false));
  }, [id]);

  function toggleAnswer(questionId, answerId) {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      const updated = current.includes(answerId)
        ? current.filter(a => a !== answerId)
        : [...current, answerId];
      return { ...prev, [questionId]: updated };
    });
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const formattedAnswers = questions.map(q => ({
        questionId: q.id,
        answerIds: answers[q.id] || [],
      }));
      const res = await api.post(`/quiz/user-trainings/${id}/attempts`, { answers: formattedAnswers });
      setResult(res.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Einreichen');
    } finally {
      setSubmitting(false);
    }
  }

  async function downloadCertificate() {
    try {
      await downloadFile(`/user-trainings/${id}/certificate`, `Zertifikat-${training?.title?.replace(/[^a-z0-9]/gi, '_')}.pdf`);
    } catch { alert('Zertifikat konnte nicht heruntergeladen werden'); }
  }

  if (loading) return <Layout title="Quiz"><div>Laden...</div></Layout>;
  if (!training || questions.length === 0) {
    return (
      <Layout title="Quiz">
        <div className="alert alert-error">Keine Fragen verfügbar.</div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/my-trainings/${id}`)}>Zurück</button>
      </Layout>
    );
  }

  if (result) {
    const wrongQuestions = (result.questionResults || []).filter(q => !q.isCorrect);
    return (
      <Layout title="Quiz — Ergebnis">
        <div className="quiz-result">
          <div className={`quiz-result-circle ${result.passed ? 'passed' : 'failed'}`}>
            <span className="quiz-result-score">{result.score.toFixed(0)}%</span>
          </div>
          <h2 className={`quiz-result-title ${result.passed ? 'passed' : 'failed'}`}>
            {result.passed ? '🎉 Bestanden!' : 'Nicht bestanden'}
          </h2>
          <p className="quiz-result-sub">
            Mindestpunktzahl: <strong>{result.passingScore}%</strong>
          </p>
          <div className="quiz-result-actions">
            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/my-trainings/${id}`)}>
              Zur Schulung zurück
            </button>
            {result.passed && (
              <button className="btn btn-secondary btn-sm" onClick={downloadCertificate}>
                Zertifikat herunterladen
              </button>
            )}
            {!result.passed && (
              <button className="btn btn-primary btn-sm" onClick={() => { setResult(null); setCurrent(0); setAnswers({}); }}>
                Erneut versuchen
              </button>
            )}
          </div>
        </div>

        {wrongQuestions.length > 0 && (
          <div style={{ maxWidth: 680, margin: '2rem auto 0' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--white)' }}>
              Falsch beantwortet ({wrongQuestions.length} von {result.questionResults.length})
            </h3>
            {wrongQuestions.map((q, i) => (
              <div key={q.questionId} className="card" style={{ marginBottom: '1rem' }}>
                <p style={{ fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                  {i + 1}. {q.questionText}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {q.answers.map(a => {
                    const wasSelected = q.submittedIds.includes(a.id);
                    const isCorrect = q.correctIds.includes(a.id);
                    let bg = 'transparent';
                    let color = 'var(--gray-dark)';
                    let prefix = '';
                    if (isCorrect) { bg = 'rgba(34,197,94,0.12)'; color = 'var(--success)'; prefix = '✓ '; }
                    else if (wasSelected) { bg = 'rgba(239,68,68,0.1)'; color = 'var(--error)'; prefix = '✗ '; }
                    return (
                      <div key={a.id} style={{ padding: '0.4rem 0.75rem', borderRadius: 6, background: bg, color, fontSize: '0.875rem' }}>
                        {prefix}{a.text}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {result.questionResults && wrongQuestions.length === 0 && result.passed && (
          <p style={{ textAlign: 'center', color: 'var(--success)', marginTop: '1rem', fontSize: '0.9rem' }}>
            Alle Fragen richtig beantwortet!
          </p>
        )}
      </Layout>
    );
  }

  const q = questions[current];
  const isLast = current === questions.length - 1;
  const selectedAnswers = answers[q.id] || [];

  return (
    <Layout title="Quiz">
      <div className="quiz-container">
        <div className="quiz-progress-bar">
          <div className="quiz-progress-fill" style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
        </div>
        <div className="quiz-progress-text">Frage {current + 1} von {questions.length}</div>

        <div className="card quiz-question-card">
          <h2 className="quiz-question-text">{q.question_text}</h2>
          <p className="quiz-question-hint">Wählen Sie alle zutreffenden Antworten aus.</p>

          <div className="quiz-answers">
            {q.answers.map(a => (
              <label key={a.id} className={`quiz-answer ${selectedAnswers.includes(a.id) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedAnswers.includes(a.id)}
                  onChange={() => toggleAnswer(q.id, a.id)}
                />
                <span>{a.answer_text}</span>
              </label>
            ))}
          </div>

          <div className="quiz-nav">
            {current > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={() => setCurrent(c => c - 1)}>← Zurück</button>
            )}
            {!isLast ? (
              <button className="btn btn-primary btn-sm" onClick={() => setCurrent(c => c + 1)}>
                Weiter →
              </button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Einreichen...' : 'Quiz abschicken'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
